// AI views: AIImportView, ValidationView, ExtractionDashView,
// RedlineReview, DraftingView.
//
// AIImportView is the bulk PDF→contract import flow with batch
// processing, AI field extraction, parent matching, and dedup checks.
// ValidationView is the human review queue for AI-extracted fields.
// ExtractionDashView shows extraction stats and quality metrics.
// RedlineReview displays AI-suggested redlines from playbook analysis.
// DraftingView is the contract drafting assistant with chat + tools.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { diffWords } from "diff";
import { T } from "../theme";
import { fmtDate, toTitleCase, hasPermission } from "../utils";
import { apiCall, apiCallEx } from "../apiClient";
import { AI_UNIVERSAL_FIELDS } from "../constants";
import { renderMarkdown } from "./markdown";
import { PdfHighlightModal } from "./PdfHighlightModal";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { RedlineReview } from "./RedlineReview";

// Validate an extraction row before allowing import. Blocks approval when:
// - A field has a value with low confidence (AI extracted something but is uncertain)
// - A field value contains placeholder markers ([brackets], "insert here",
//   "TBD", "to be determined", etc.) — the AI hallucinated a placeholder
// - A field is empty but the AI is confident it SHOULD be present (rare)
//
// Does NOT block on: empty field + low confidence. That's the AI saying the
// field is not applicable / not in this document — a normal case (e.g., an MSA
// doesn't have "Amendment Effective Date" or "Sub-processor Requirements").
// Returns { ok, lowConf: string[], missing: string[], placeholder: string[] }.
const PLACEHOLDER_RE = /[\[\]<>{}]|insert\s|tbd|to\s+be\s+determined|placeholder|\bn\/?a\b/i;
const validateImportRow = (row: any): { ok: boolean; lowConf: string[]; missing: string[]; placeholder: string[]; blocking: string[] } => {
  const lowConf: string[] = [];
  const missing: string[] = [];
  const placeholder: string[] = [];
  Object.entries(row).forEach(([k, v]: [string, any]) => {
    if (k.startsWith('__') || k === 'suggestedRelationship') return;
    if (!v || typeof v !== 'object') return;
    const val = (v.v == null ? '' : String(v.v)).trim();
    const conf = (v.conf || v.confidence || 'medium').toLowerCase();
    if (!val) { if (conf !== 'low') missing.push(k); return; }
    if (PLACEHOLDER_RE.test(val)) { placeholder.push(k); return; }
    if (conf === 'low') { lowConf.push(k); }
  });
  const blocking = [...missing, ...placeholder, ...lowConf];
  return { ok: blocking.length === 0, lowConf, missing, placeholder, blocking };
};


/* ═══════════════════ DRAFTING WORKSPACE ═══════════════════ */
export const DraftingView = ({ user = null, targetRequestId = undefined, onTargetConsumed = undefined, persistedJobId = undefined, onJobChange = undefined, persistedRequestId = undefined, onRequestSelect = undefined }: { user?: UserRow | null; targetRequestId?: string; onTargetConsumed?: () => void; persistedJobId?: string; onJobChange?: (id: string) => void; persistedRequestId?: string; onRequestSelect?: (id: string) => void }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequestRaw] = useState<any>(null);
  const setSelectedRequest = (req: any) => { setSelectedRequestRaw(req); if (onRequestSelect) onRequestSelect?.(req?.id || null); };
  const [chatProvider, setChatProvider] = useState("claude");
  const [harveyAvailable, setHarveyAvailable] = useState(false);
  useEffect(() => { apiCall('/settings/harvey/status').then(r => { if (r?.available) setHarveyAvailable(true); }).catch(() => {}); }, []);
  const [showWorkload, setShowWorkload] = useState(false);
  const [workloadItems, setWorkloadItems] = useState<any[]>([]);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [requestDetail, setRequestDetail] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [activeDocId, setActiveDocId] = useState<any>(null);
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState("");
  const [thinkingSnippet, setThinkingSnippet] = useState("");
  const [redlineGenerating, setRedlineGenerating] = useState(false);
  const [chatAttachment, setChatAttachment] = useState<any>(null);
  const [redlineReview, setRedlineReview] = useState<any>(null);
  const [redlineDocxPath, setRedlineDocxPath] = useState<any>(null);
  const [redlineWorkspaceMessage, setRedlineWorkspaceMessage] = useState<{ tone: "info" | "problem"; text: string } | null>(null);
  // Which request is on screen right now, readable from inside an in-flight
  // request that started before the user switched away.
  const selectedRequestIdRef = useRef<any>(null);
  const [analyzingRedline, setAnalyzingRedline] = useState<any>(null);
  const [convertingToRedline, setConvertingToRedline] = useState<any>(null);
  const [bgJobId, setBgJobIdRaw] = useState(persistedJobId || null);
  const setBgJobId = (id: string) => { setBgJobIdRaw(id); if (onJobChange) onJobChange?.(id); };
  const bgPollRef = useRef<any>(null);
  const [playbooks, setPlaybooks] = useState<any[]>([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState("");
  const [promptTemplates, setPromptTemplates] = useState<any[]>([]);
  const [showPromptPicker, setShowPromptPicker] = useState(false);
  const [selectedSystemPrompt, setSelectedSystemPrompt] = useState<any>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [preOptimizeInput, setPreOptimizeInput] = useState<any>(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [savePromptForm, setSavePromptForm] = useState<Record<string, any>>({ name: '', category: 'Custom' });
  const messagesEndRef = useRef<any>(null);
  const fileInputRef = useRef<any>(null);
  const chatFileInputRef = useRef<any>(null);
  const searchTimer = useRef<any>(null);

  useEffect(() => { if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Fetch playbooks and prompt templates
  useEffect(() => {
    apiCall('/playbooks').then(r => { if (r?.playbooks) setPlaybooks(r.playbooks.filter((p: any) => p.is_active)); });
    apiCall('/prompts').then(r => { if (r?.prompts) setPromptTemplates(r.prompts); });
  }, []);

  // Restore selected request from persisted ID (when navigating back to drafting view).
  // Skip if a targetRequestId is pending — that effect will handle selection instead.
  // Uses a ref to track what we've already restored to avoid duplicate loads.
  const restoredRequestIdRef = useRef<any>(null);
  useEffect(() => {
    if (targetRequestId) return; // target effect will handle it
    if (!persistedRequestId) return;
    if (restoredRequestIdRef.current === persistedRequestId) return; // already restored this one
    restoredRequestIdRef.current = persistedRequestId;
    apiCall(`/requests/${persistedRequestId}`).then(r => {
      if (r?.request) {
        selectRequest(r.request);
      }
    });
  }, [persistedRequestId, targetRequestId]);

  // Check for active background redline jobs when selected request changes
  useEffect(() => {
    // Clear stale state from previous request
    setBgJobId(''); setAnalyzingRedline(''); setStreamStatus(""); setRedlineReview(null); setRedlineDocxPath('');
    setRedlineWorkspaceMessage(null);
    selectedRequestIdRef.current = selectedRequest?.id || null;
    if (!selectedRequest) return;
    apiCall(`/extraction/redline-jobs/active?requestId=${selectedRequest.id}`).then(r => {
      if (!r?.jobs) return;
      const active = r.jobs.find((j: any) => j.status === 'processing');
      const recent = !active ? r.jobs.find((j: any) => j.status === 'completed') : null;
      if (active) {
        setBgJobId(active.id);
        setAnalyzingRedline(`${active.stage || 'Processing...'} (${active.progress || 0}%)`);
        if (active.thinking) setStreamStatus(active.thinking);
      } else if (recent) {
        setBgJobId(recent.id);
        setAnalyzingRedline('Loading completed review...');
        apiCall(`/extraction/playbook-result/${recent.id}`).then(d => {
          if (d) { setRedlineReview(d); setRedlineDocxPath(d.docxPath); }
          setAnalyzingRedline(''); setBgJobId('');
        });
      }
    });
  }, [selectedRequest?.id]);

  // Poll background job
  useEffect(() => {
    if (!bgJobId) { if (bgPollRef.current) clearInterval(bgPollRef.current); return; }
    const poll = async () => {
      const r = await apiCall(`/extraction/redline-job/${bgJobId}`);
      if (!r?.job) return;
      const j = r.job;
      if (j.status === 'processing') {
        setAnalyzingRedline(`${j.stage || 'Processing...'} (${j.progress || 0}%)`);
        if (j.thinking) setStreamStatus(j.thinking);
      } else if (j.status === 'completed') {
        setAnalyzingRedline('Loading results...');
        const data = await apiCall(`/extraction/playbook-result/${bgJobId}`);
        if (data) { setRedlineReview(data); setRedlineDocxPath(data.docxPath); }
        setAnalyzingRedline(''); setStreamStatus(""); setBgJobId('');
      } else if (j.status === 'failed') {
        setAnalyzingRedline(''); setStreamStatus(""); setBgJobId('');
        alert('Background review failed: ' + (j.error_message || 'Unknown error'));
      }
    };
    poll();
    bgPollRef.current = setInterval(poll, 3000);
    return () => { if (bgPollRef.current) clearInterval(bgPollRef.current); };
  }, [bgJobId]);

  const searchRequests = (q: string) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      const data = await apiCall(`/requests?search=${encodeURIComponent(q)}&limit=10`);
      if (data?.requests) setSearchResults(data.requests);
    }, 300);
  };

  const loadWorkload = async () => {
    setLoadingWorkload(true);
    setShowWorkload(true);
    const r = await apiCall(`/requests?assigned_to=${user!.id}&limit=20`);
    if (r?.requests) {
      setWorkloadItems(r.requests.filter((req: RequestRow) => !['fully_executed', 'cancelled', 'closed', 'question_answered'].includes((req!.status?.toLowerCase?.()?.replace(/ /g, '_')) || '')));
    }
    setLoadingWorkload(false);
  };

  const selectRequest = async (r: RequestRow) => {
    setSelectedRequest(r);
    setSearchResults([]);
    setSearchQuery("");
    setMessages([]);
    setAttachedFiles([]);
    setActiveDocId(null);
    setChatAttachment(null);
    const detail = await apiCall(`/requests/${r.id}`);
    setRequestDetail(detail);
    const verData = await apiCall(`/versions/request/${r.id}`);
    if (verData?.versions) {
      setDocuments(verData.versions.map((v: any) => ({ id: v.id, name: v.filename, type: v.file_type, size: v.file_size, versionNum: v.version_number, label: !v.label || v.label === 'current_draft' ? 'Initial Draft' : v.label.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()), source: "request" })));
    }
  };

  // Auto-select request when navigated from "Open in Drafting"
  useEffect(() => {
    if (!targetRequestId) return;
    (async () => {
      try {
        const data = await apiCall(`/requests/${targetRequestId}`);
        if (data?.request) {
          selectRequest(data.request);
          restoredRequestIdRef.current = data.request.id; // prevent restore effect from re-loading
        }
      } catch (err: any) { console.error('Failed to load target request:', err); }
      if (onTargetConsumed) onTargetConsumed?.();
    })();
  }, [targetRequestId]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from((e.target as HTMLInputElement).files || []);
    files.forEach((file: any) => {
      const ext = (file!.name.split('.').pop() || '').toLowerCase();
      if (!['pdf','docx','doc'].includes(ext)) { alert('Only PDF and Word documents are supported'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const id = `attached_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        setAttachedFiles(prev => [...prev, { id, name: file.name, type: ext, size: file.size, base64: (reader.result as string).split(',')[1], source: "attached" }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleChatAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files![0];
    if (!file) return;
    const ext = (file!.name.split('.').pop() || '').toLowerCase();
    if (!['pdf','docx','doc'].includes(ext)) { alert('Only PDF and Word documents are supported'); return; }
    const reader = new FileReader();
    reader.onload = () => setChatAttachment({ name: file.name, base64: (reader.result as string).split(',')[1], size: file.size });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePlaybookReview = async (isDirect = false) => {
    const verData = requestDetail?.versions || [];
    const docxVer = verData.find((v: any) => v.filename?.endsWith('.docx') || v.file_path?.endsWith('.docx'));
    const docxPath = docxVer?.file_path;
    if (!docxPath) { alert('No .docx file found for this request. Upload a Word document first.'); return; }
    const pbId = selectedPlaybook || 'cda_certara';
    // Start background job (same pattern as AI Import)
    const r = await apiCall('/extraction/redline-job/start', {
      method: 'POST',
      body: JSON.stringify({
        requestId: selectedRequest?.id,
        docxPath, playbookSlug: pbId,
        counterpartyName: selectedRequest?.counterparty || 'Counterparty',
        methodology: selectedSystemPrompt?.prompt_text || undefined,
        directXml: isDirect || false,
      })
    });
    if (r?.job) {
      setBgJobId(r.job.id);
      setAnalyzingRedline('Starting review...');
    } else {
      alert('Failed to start review');
    }
  };

  const handleChatToRedline = async () => {
    const verData = requestDetail?.versions || [];
    const docxVer = verData.find((v: any) => v.filename?.endsWith('.docx') || v.file_path?.endsWith('.docx'));
    const docxPath = docxVer?.file_path || null;
    if (!docxPath) { alert('No .docx file found for this request. The redline requires a Word document to match text against.'); return; }
    const chatMsgs = messages.map((m: any) => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content || m.text || '' })).filter(m => m.content.length > 0);
    if (chatMsgs.length < 2) { alert('Have a conversation about the contract first, then convert the recommendations to a redline.'); return; }
    setConvertingToRedline('Extracting recommendations from conversation...');
    try {
      const token = localStorage.getItem('kontractai_token');
      await new Promise(r => setTimeout(r, 300));
      setConvertingToRedline('Converting recommendations to structured revisions...');
      const resp = await fetch('/api/extraction/chat-to-revisions', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messages: chatMsgs, docxPath, counterpartyName: selectedRequest?.counterparty || 'Counterparty' }),
      });
      if (!resp.ok) { const errData = await resp.json().catch(() => ({})); throw new Error(errData.error || `Conversion failed: ${resp.status}`); }
      setConvertingToRedline('Building revision cards...');
      await new Promise(r => setTimeout(r, 500));
      const data = await resp.json();
      setRedlineReview({ ...data, playbookName: 'Chat Recommendations', counterpartyName: selectedRequest?.counterparty || data.counterpartyName || 'Counterparty' });
      setRedlineDocxPath(docxPath);
    } catch (err: any) { console.error('Chat-to-redline error:', err); alert('Failed to convert chat to redline: ' + (err.message || 'Unknown error')); }
    finally { setConvertingToRedline(null); }
  };

  const allDocs = [...documents, ...attachedFiles];
  const openHarveyRedlineWorkspace = () => {
    setRedlineWorkspaceMessage({
      tone: "info",
      text: "Harvey Redline now runs from Document Intelligence. Open the matter there, choose Redline, select a Word document, and generate the tracked-change DOCX from the saved matter files.",
    });
  };

  const sendMessage = async (text: string | undefined = undefined) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const attachLabel = chatAttachment ? ` [attached: ${chatAttachment.name}]` : "";
    setMessages(prev => [...prev, { role: "user", text: msg + attachLabel }]);
    setLoading(true);
    setStreamStatus("");
    setThinkingSnippet("");

    // Progress timer for UX
    let stage = 0;
    const stages = ["Reading documents...", "Analyzing contract provisions...", "Reviewing key clauses...", "Formulating recommendations...", "Drafting response..."];
    setStreamStatus(stages[0]);
    const progressTimer = setInterval(() => { stage = Math.min(stage + 1, stages.length - 1); setStreamStatus(stages[stage]); }, 5000);

    const apiMsgs = [...messages.map((m: any) => ({ role: m.role, content: m.text })), { role: "user", content: msg }];
    // Only send client-attached docs (request versions are loaded server-side from disk)
    const docsToSend = allDocs.filter(d => d.base64 && d.source === "attached").map(d => ({ base64: d.base64, name: d.name }));
    if (chatAttachment && !docsToSend.some(d => d.name === chatAttachment.name)) {
      docsToSend.push(chatAttachment);
    }

    const body: Record<string, any> = { requestId: selectedRequest?.id, messages: apiMsgs, documents: docsToSend, playbookSlug: selectedPlaybook || undefined, systemPromptTemplate: selectedSystemPrompt?.prompt_text || undefined, ...(chatProvider === 'harvey' ? { provider: 'harvey' } : {}) };

    try {
      const r = await apiCall("/extraction/chat/drafting", { method: "POST", body: JSON.stringify(body) });
      clearInterval(progressTimer);
      if (r?.response) {
        setMessages(prev => [...prev, { role: "assistant", text: r.response, provider: r.provider, sources: r.sources }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", text: "Error: " + (r?.error || "Could not get a response. Please try again.") }]);
      }
    } catch (err: any) {
      clearInterval(progressTimer);
      setMessages(prev => [...prev, { role: "assistant", text: "Error: Connection failed. Please try again." }]);
    }

    setLoading(false);
    setStreamStatus("");
    setThinkingSnippet("");
    setChatAttachment(null);
  };

  const generateRedline = async (chatResponse: any) => {
    alert("Redline generation with tracked changes is being upgraded for better Word compatibility. This feature will be available soon.");
    return;
    if (!window.confirm("Generate a Word document with tracked changes from this analysis?")) return;
    const docxDocs = allDocs.filter(d => d.base64 && (d.type === "docx" || d.type === "doc" || d.name?.endsWith(".docx") || d.name?.endsWith(".doc")));
    if (docxDocs.length === 0) { alert("No DOCX document available for redline. Please attach a Word document (.docx) in the left panel."); return; }
    let targetDoc = null;
    if (activeDocId) { const ad = allDocs.find(d => d.id === activeDocId); if (ad && (ad.type === "docx" || ad.type === "doc")) targetDoc = ad; }
    if (!targetDoc) targetDoc = docxDocs[0];

    setRedlineGenerating(true);
    let stage = 0;
    const stages = ["Extracting revision instructions...", "Analyzing document structure...", "Applying tracked changes...", "Building redline document..."];
    setStreamStatus(stages[0]);
    const timer = setInterval(() => { stage = Math.min(stage + 1, stages.length - 1); setStreamStatus(stages[stage]); }, 6000);

    const r = await apiCall(`/versions/request/${selectedRequest.id}/generate-redline`, { method: "POST", body: JSON.stringify({ chatResponse, attachedDocument: { base64: targetDoc.base64, name: targetDoc.name } }) });

    clearInterval(timer);
    setRedlineGenerating(false);
    setStreamStatus("");

    if (r?.version) {
      setMessages(prev => [...prev, { role: "assistant", text: `Redline generated with ${r.revisionCount} tracked changes. Saved as version ${r.version.version_number}. Open it in Word to review and accept/reject changes.` }]);
      const verData = await apiCall(`/versions/request/${selectedRequest.id}`);
      if (verData?.versions) setDocuments(verData.versions.map((v: any) => ({ id: v.id, name: v.filename, type: v.file_type, size: v.file_size, versionNum: v.version_number, label: !v.label || v.label === 'current_draft' ? 'Initial Draft' : v.label.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()), source: "request" })));
    } else {
      setMessages(prev => [...prev, { role: "assistant", text: "Redline generation failed: " + (r?.error || "Unknown error") }]);
    }
  };

  const statusColors: Record<string, string> = { "Draft": T.txM, "Pending Review": T.ylw, "In Review": T.blu, "Approved": T.grn, "Sent for Signature": T.acc, "Executed": T.grn, "Rejected": T.red, "On Hold": T.txD, "Cancelled": T.txD };

  return (
    <div style={{ display: "flex", height: "100%", minHeight: "calc(100vh - 60px)" }}>
      {/* Left Panel */}
      <div style={{ width: 300, borderRight: `1px solid ${T.brd}`, display: "flex", flexDirection: "column", backgroundColor: T.bg2, flexShrink: 0 }}>
        <div style={{ padding: 16, borderBottom: `1px solid ${T.brd}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 8 }}>Drafting Workspace</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 10, color: T.txD }}>Model:</div>
            {harveyAvailable && <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.brd}`, marginRight: 4 }}>
              <button onClick={() => setChatProvider('claude')} style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: chatProvider === 'claude' ? T.acc : 'transparent', color: chatProvider === 'claude' ? '#fff' : T.txM }}>Claude</button>
              <button onClick={() => setChatProvider('harvey')} style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: chatProvider === 'harvey' ? 'rgb(99,102,241)' : 'transparent', color: chatProvider === 'harvey' ? '#fff' : T.txM }}>Harvey</button>
            </div>}
            {/* MT-009: model choice is operator-managed (admin AI Routing). The old
                dropdown offered dead model ids and was cosmetic — the server ignores
                caller models on these paths. */}
            <span style={{ padding: "4px 6px", fontSize: 10, color: T.txM }}>Model: managed by AI Routing</span>
          </div>
          {playbooks.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
              <div style={{ fontSize: 10, color: T.txD }}>Playbook:</div>
              <select value={selectedPlaybook} onChange={e => setSelectedPlaybook(e.target.value)} style={{ padding: "4px 6px", fontSize: 10, border: `1px solid ${T.brd}`, borderRadius: 4, background: T.bg3, color: T.tx, fontFamily: "inherit", cursor: "pointer", flex: 1 }}>
                <option value="">Select playbook...</option>
                {playbooks.map((p: any) => {
                  const reqType = selectedRequest?.type || selectedRequest?.request_type || '';
                  const recommended = (p.contract_types || []).some((ct: any) => reqType.toLowerCase().includes(ct.toLowerCase()) || ct.toLowerCase().includes(reqType.toLowerCase()));
                  return <option key={p.slug} value={p.slug}>{p.name}{recommended ? ' (Recommended)' : ''}</option>;
                })}
              </select>
            </div>
          )}
          {!selectedRequest ? (
            <div>
              <Btn v="secondary" sz="sm" onClick={() => { loadWorkload(); }} disabled={loadingWorkload} style={{ width: "100%", marginBottom: 8 }}>{loadingWorkload ? "Loading..." : "My Workload"}</Btn>
              {showWorkload && workloadItems.length > 0 && (
                <div style={{ marginBottom: 8, maxHeight: 250, overflow: "auto" }}>
                  <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>Your assigned requests ({workloadItems.length})</div>
                  {workloadItems.map(r => (
                    <div key={r.id} onClick={() => { selectRequest(r); setShowWorkload(false); }} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 2 }}>{r.title}</div>
                      <div style={{ fontSize: 10, color: T.txM }}>{r.counterparty} - {r.status?.replace?.(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              )}
              {showWorkload && workloadItems.length === 0 && !loadingWorkload && <div style={{ fontSize: 11, color: T.txD, marginBottom: 8, textAlign: "center" }}>No active assigned requests</div>}
              <div style={{ fontSize: 10, color: T.txD, textAlign: "center", marginBottom: 8 }}>or search</div>
              <input value={searchQuery} onChange={e => searchRequests(e.target.value)} placeholder="Search requests..." style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              {searchResults.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 300, overflowY: "auto" }}>
                  {searchResults.map(r => (
                    <div key={r.id} onClick={() => selectRequest(r)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 2 }}>{r.title}</div>
                      <div style={{ fontSize: 10, color: T.txM }}>{r.counterparty} - {r.status?.replace?.(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.acc}`, backgroundColor: T.accS, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.tx, marginBottom: 4 }}>{selectedRequest.title}</div>
                <div style={{ fontSize: 11, color: T.txM, marginBottom: 2 }}>{selectedRequest.counterparty}</div>
                <Badge c={statusColors[selectedRequest.status || ''] || T.txM} b={(statusColors[selectedRequest.status || ''] || T.txM) + "22"}>{selectedRequest.status}</Badge>
              </div>
              <Btn v="secondary" sz="sm" onClick={() => { setSelectedRequest(null); setRequestDetail(null); setDocuments([]); setMessages([]); setAttachedFiles([]); setActiveDocId(null); setChatAttachment(null); }} style={{ width: "100%", marginBottom: 8 }}>Change Request</Btn>
              <button
                onClick={openHarveyRedlineWorkspace}
                style={{ width: "100%", padding: "9px 10px", fontSize: 11, fontWeight: 700, border: `2px solid ${T.prp}`, borderRadius: 8, background: T.prpS, color: T.prp, cursor: "pointer", fontFamily: "inherit" }}
              >
                Harvey Redline
              </button>
              <div style={{ fontSize: 10, color: T.txD, lineHeight: 1.5, marginTop: 4, marginBottom: 8 }}>
                Generate tracked-change DOCX reviews from the Document Intelligence Redline workspace.
              </div>
              {redlineWorkspaceMessage && (
                <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 6, fontSize: 11, lineHeight: 1.5, border: `1px solid ${redlineWorkspaceMessage.tone === "problem" ? T.red : T.acc}`, backgroundColor: redlineWorkspaceMessage.tone === "problem" ? T.redS : T.accS, color: redlineWorkspaceMessage.tone === "problem" ? T.red : T.acc }}>
                  {redlineWorkspaceMessage.text}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedRequest && (
          <>
            {requestDetail?.request?.description && (
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.brd}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>DESCRIPTION</div>
                <div style={{ fontSize: 11, color: T.txM, lineHeight: 1.5, maxHeight: 100, overflowY: "auto" }}>{requestDetail.request.description}</div>
              </div>
            )}
            <div style={{ flex: 1, padding: "12px 16px", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txD }}>DOCUMENTS</div>
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleAttachFiles} accept=".pdf,.docx,.doc" multiple style={{ display: "none" }} />
                  <button onClick={() => fileInputRef.current?.click()} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>+ Attach</button>
                </div>
              </div>
              {allDocs.length === 0 && <div style={{ fontSize: 11, color: T.txD, textAlign: "center", padding: 16 }}>No documents yet</div>}
              {allDocs.map(d => (
                <div key={d.id} onClick={() => setActiveDocId(activeDocId === d.id ? null : d.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, cursor: "pointer", marginBottom: 4, backgroundColor: activeDocId === d.id ? T.accS : "transparent", border: `1px solid ${activeDocId === d.id ? T.acc : T.brd}` }}>
                  <span style={{ fontSize: 12 }}>{d.type === "pdf" ? "PDF" : "DOC"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: T.tx, fontWeight: activeDocId === d.id ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 9, color: T.txD }}>{d.source === "request" ? `v${d.versionNum} - ${d.label}` : "Attached"}{d.size ? ` - ${(d.size / 1024).toFixed(0)} KB` : ""}</span>
                      {d.source === "attached" && d.base64 ? (
                        <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); const bytes = atob(d.base64); const arr = new Uint8Array(bytes.length); for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i); const blob = new Blob([arr]); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = d.name; a.click(); URL.revokeObjectURL(url); }} style={{ fontSize: 9, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>↓</button>
                      ) : (d.id || d.versionId) ? (
                        <button onClick={async (e) => { e.stopPropagation(); const token = localStorage.getItem("kontractai_token"); const resp = await fetch(`/api/versions/${d.id || d.versionId}/download`, { headers: { Authorization: `Bearer ${token}` } }); if (!resp.ok) { alert("Download failed"); return; } const blob = await resp.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = d.name; a.click(); URL.revokeObjectURL(url); }} style={{ fontSize: 9, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>↓</button>
                      ) : null}
                    </div>
                  </div>
                  {d.source === "attached" && (
                    <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAttachedFiles(prev => prev.filter((f: any) => f.id !== d.id)); if (activeDocId === d.id) setActiveDocId(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 11, fontWeight: 600 }}>x</button>
                  )}
                  {d.source === "request" && !d.locked && (hasPermission(user, 'requests.delete') || ["admin", "legalops"].includes(user?.role || '')) && (
                    <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Delete "${d.name}"?`)) return; const vid = d.id || d.versionId; const r = await apiCall(`/versions/${vid}`, { method: "DELETE" }); if (r?.message) { const verData = await apiCall(`/versions/request/${selectedRequest.id}`); if (verData?.versions) { setDocuments(prev => [...verData.versions.map((v: any) => ({ id: v.id, name: v.filename, type: v.file_type, size: v.file_size, versionNum: v.version_number, label: !v.label || v.label === 'current_draft' ? 'Initial Draft' : v.label.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()), source: "request" })), ...prev.filter((x: any) => x.source === "attached")]); } } else { alert(r?.error || "Delete failed"); } }} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 11, fontWeight: 600 }}>x</button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right Panel */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", backgroundColor: T.bg2 }}>
        {!selectedRequest ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✏</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.tx, marginBottom: 8 }}>Drafting Workspace</div>
            <div style={{ fontSize: 13, color: T.txM, textAlign: "center", maxWidth: 400 }}>Search and select a contract request to start drafting. AI will help you review documents, draft language, and generate redlines.</div>
          </div>
        ) : redlineReview ? (
          <RedlineReview analysisData={redlineReview} docxPath={redlineDocxPath} onBack={() => { setRedlineReview(null); setRedlineDocxPath(''); }} onGenerateComplete={() => { setRedlineReview(null); setRedlineDocxPath(''); }} />
        ) : (
          <>
            <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ fontSize: 14, color: T.txD, marginBottom: 8 }}>Start drafting for "{selectedRequest.title}"</div>
                  <div style={{ fontSize: 11, color: T.txD, marginBottom: 20 }}>The AI has access to the request details{documents.length > 0 ? " and attached documents" : ""}.</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                    {["Draft the initial agreement based on the request details", "Summarize the key terms and obligations", "What risks should I flag for this agreement?", "Review the indemnification and liability provisions", "Recommend revisions to strengthen our position", "Draft a response to the counterparty's position"].map(q => (
                      <button key={q} onClick={() => sendMessage(q)} style={{ padding: "6px 12px", fontSize: 11, border: `1px solid ${T.brd}`, borderRadius: 16, background: "transparent", color: T.txM, cursor: "pointer", fontFamily: "inherit" }}>{q}</button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
                    {allDocs.some(d => d.base64 && (d.type === "docx" || d.type === "doc" || d.name?.endsWith(".docx"))) && (
                      <button onClick={() => { const desc = requestDetail?.request?.description || ""; sendMessage((desc ? desc + "\n\n" : "") + "Review the attached documents and recommend specific revisions. For each change, provide the exact current language and the exact revised language."); }} style={{ padding: "8px 16px", fontSize: 11, border: "2px solid #c0392b", borderRadius: 8, background: "#fef2f2", color: "#c0392b", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Draft & Generate Redline</button>
                    )}
                    <button onClick={() => handlePlaybookReview(false)} disabled={!!bgJobId} style={{ padding: "8px 16px", fontSize: 11, border: `2px solid ${T.acc}`, borderRadius: 8, background: T.accS, color: T.acc, cursor: bgJobId ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, opacity: bgJobId ? 0.6 : 1 }}>{bgJobId ? "Running..." : "Playbook Review"}</button>
                    <button onClick={() => handlePlaybookReview(true)} disabled={!!bgJobId} style={{ padding: "8px 16px", fontSize: 11, border: "2px solid #c0392b", borderRadius: 8, background: "#fef2f2", color: "#c0392b", cursor: bgJobId ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, opacity: bgJobId ? 0.6 : 1 }}>{bgJobId ? "Running..." : "Direct Redline"}</button>
                  </div>
                  {(analyzingRedline || bgJobId) && (
                    <div style={{ marginTop: 12, padding: "12px 16px", backgroundColor: T.accS, border: `1px solid ${T.acc}22`, borderRadius: 8, textAlign: "center" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.acc, marginBottom: 4 }}>{analyzingRedline || 'Processing in background...'}</div>
                      {streamStatus && <div style={{ fontSize: 11, color: T.txM, fontStyle: "italic", maxHeight: 40, overflow: "hidden" }}>{streamStatus}</div>}
                      {bgJobId && <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>Safe to navigate away. Results will be ready when you return.</div>}
                    </div>
                  )}
                </div>
              )}
              {messages.map((m: any, i: number) => (
                <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
                  <div style={{ maxWidth: "80%", padding: "12px 16px", borderRadius: m.role === "user" ? "14px 14px 2px 14px" : "14px 14px 14px 2px", backgroundColor: m.role === "user" ? T.acc : T.bg3, color: m.role === "user" ? "#fff" : T.tx, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                    {m.role === "assistant" ? <>{renderMarkdown(m.text)}{m.provider === 'harvey' && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '2px 8px', borderRadius: 10, backgroundColor: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.15)', fontSize: 10, color: 'rgb(99,102,241)' }}>Harvey AI</div>}{m.sources && m.sources.length > 0 && <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${T.brd}` }}><div style={{ fontSize: 9, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: 'uppercase' }}>Sources</div>{m.sources.map((s: any, si: number) => <div key={si} style={{ fontSize: 10, color: T.txM, padding: '2px 6px', backgroundColor: T.bg3, borderRadius: 3, marginBottom: 2 }}><strong>[{s.citation_num || si+1}]</strong> {s.document_name || s.title || 'Source'}{s.page ? `, p.${s.page}` : ''}</div>)}</div>}</> : m.text}
                    {m.role === "assistant" && m.text && !m.text.startsWith("Redline generated") && !m.text.startsWith("Error:") && !m.text.startsWith("The connection was interrupted") && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8, borderTop: `1px solid ${T.brd}`, paddingTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button onClick={() => navigator.clipboard.writeText(m.text)} style={{ fontSize: 10, color: T.txD, background: "none", border: "none", cursor: "pointer" }}>Copy</button>
                        {m.text.length > 200 && <ChatExportButtons content={m.text} />}
                        {m.text.length > 200 && allDocs.some(d => d.base64 && (d.type === "docx" || d.type === "doc" || d.name?.endsWith(".docx") || d.name?.endsWith(".doc"))) && (
                          <button disabled title="Redline generation is being upgraded for better Word compatibility. Coming soon." style={{ fontSize: 10, color: "#bbb", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 4, padding: "2px 8px", cursor: "not-allowed", fontWeight: 600 }}>Generate Redline (Coming Soon)</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(loading || redlineGenerating) && <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}><div style={{ padding: "12px 16px", borderRadius: "14px 14px 14px 2px", backgroundColor: T.bg3, color: T.txM, fontSize: 13, maxWidth: "80%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 14, height: 14, border: `2px solid ${redlineGenerating ? "#c0392b" : T.acc}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                  <span style={{ fontWeight: 500 }}>{streamStatus || (redlineGenerating ? "Generating redline..." : "Thinking...")}</span>
                </div>
              </div></div>}
              <div ref={messagesEndRef} />
            </div>
            {chatAttachment && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 16px", backgroundColor: T.accS, fontSize: 11, color: T.acc, margin: "0 20px", borderRadius: 6 }}>
                <span>📎</span> {chatAttachment.name} ({(chatAttachment.size / 1024).toFixed(0)} KB)
                <button onClick={() => setChatAttachment(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 12, fontWeight: 600, marginLeft: "auto" }}>x</button>
              </div>
            )}
            {messages.length >= 2 && messages.some((m: any) => m.role === 'assistant') && !convertingToRedline && !analyzingRedline && (
              <div style={{ padding: '10px 20px', borderTop: `1px solid ${T.grn}40`, backgroundColor: T.grnS, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: T.txM }}>Ready to generate tracked changes?</span>
                <button onClick={() => { handleChatToRedline(); }} style={{ padding: '7px 18px', fontSize: 12, border: `2px solid ${T.grn}`, borderRadius: 6, background: T.grn, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>Convert to Redline</button>
              </div>
            )}
            {convertingToRedline && (
              <div style={{ padding: '10px 20px', borderTop: `1px solid ${T.brd}`, textAlign: 'center' }}>
                <span style={{ fontSize: 12, color: T.acc, fontWeight: 600 }}>{convertingToRedline}</span>
              </div>
            )}
            {/* Prompt templates bar */}
            {promptTemplates.length > 0 && (
              <div style={{ padding: "4px 20px", borderTop: `1px solid ${T.brd}`, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <button onClick={() => setShowPromptPicker(!showPromptPicker)} style={{ fontSize: 10, color: T.acc, background: "none", border: `1px solid ${T.acc}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>{showPromptPicker ? 'Hide' : 'Prompts'}</button>
                {showPromptPicker && promptTemplates.map(p => (
                  <button key={p.id} onClick={() => {
                    if (p.inject_as_system) { setSelectedSystemPrompt((prev: any) => prev?.id === p.id ? null : p); }
                    else { setInput(p.prompt_text); }
                    setShowPromptPicker(false);
                  }} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 12, border: `1px solid ${p.inject_as_system ? (selectedSystemPrompt?.id === p.id ? T.grn : T.orn) : T.brd}`, background: selectedSystemPrompt?.id === p.id ? T.grnS : T.bg3, color: p.inject_as_system ? (selectedSystemPrompt?.id === p.id ? T.grn : T.orn) : T.txM, cursor: "pointer", fontFamily: "inherit" }} title={p.inject_as_system ? 'System prompt: ' + p.name : p.prompt_text?.substring(0, 100)}>{p.inject_as_system ? '⚙ ' : ''}{p.name}</button>
                ))}
              </div>
            )}
            {selectedSystemPrompt && (
              <div style={{ padding: "4px 20px", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, color: T.grn }}>Active methodology:</span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, border: `1px solid ${T.grn}`, color: T.grn, background: T.grnS }}>{selectedSystemPrompt.name}</span>
                <button onClick={() => setSelectedSystemPrompt(null)} style={{ fontSize: 10, color: T.txD, background: "none", border: "none", cursor: "pointer" }}>x</button>
              </div>
            )}
            {/* Undo optimize banner */}
            {preOptimizeInput !== null && (
              <div style={{ padding: "4px 20px", backgroundColor: T.grnS, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: T.grn }}>Prompt optimized</span>
                <button onClick={() => { setInput(preOptimizeInput); setPreOptimizeInput(null); }} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Undo</button>
              </div>
            )}
            {/* Save prompt mini-form */}
            {showSavePrompt && (
              <div style={{ padding: "8px 20px", borderTop: `1px solid ${T.brd}`, display: "flex", gap: 6, alignItems: "center" }}>
                <input value={savePromptForm.name} onChange={e => setSavePromptForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name..." style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: `1px solid ${T.brd}`, fontSize: 11, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit" }} />
                <select value={savePromptForm.category} onChange={e => setSavePromptForm(f => ({ ...f, category: e.target.value }))} style={{ padding: "4px 6px", fontSize: 10, border: `1px solid ${T.brd}`, borderRadius: 4, background: T.bg3, color: T.tx, fontFamily: "inherit" }}>
                  {['Review', 'Drafting', 'Analysis', 'Extraction', 'Custom'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <Btn v="primary" sz="sm" onClick={async () => {
                  if (!savePromptForm.name || !input.trim()) return;
                  const r = await apiCall('/prompts', { method: 'POST', body: JSON.stringify({ name: savePromptForm.name, prompt_text: input.trim(), category: savePromptForm.category }) });
                  if (r?.prompt) { setPromptTemplates(prev => [...prev, r.prompt]); setShowSavePrompt(false); setSavePromptForm({ name: '', category: 'Custom' }); }
                }}>Save</Btn>
                <button onClick={() => setShowSavePrompt(false)} style={{ fontSize: 10, color: T.txD, background: "none", border: "none", cursor: "pointer" }}>x</button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, padding: "12px 20px", borderTop: `1px solid ${T.brd}`, alignItems: "center" }}>
              <input type="file" ref={chatFileInputRef} onChange={handleChatAttach} accept=".pdf,.docx,.doc" style={{ display: "none" }} />
              <button onClick={() => chatFileInputRef.current?.click()} style={{ padding: "8px 10px", background: "none", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", color: chatAttachment ? T.acc : T.txD, fontSize: 14 }} title="Attach a document">📎</button>
              <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={loading} />
              <input value={input} onChange={e => { setInput(e.target.value); setPreOptimizeInput(null); }} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Ask AI to draft, review, or revise..." style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
              {/* Optimize button */}
              <button onClick={async () => {
                if (!input.trim() || optimizing) return;
                setOptimizing(true);
                const r = await apiCall('/prompts/optimize', { method: 'POST', body: JSON.stringify({ prompt: input.trim() }) });
                setOptimizing(false);
                if (r?.optimized) { setPreOptimizeInput(input); setInput(r.optimized); }
              }} disabled={!input.trim() || optimizing} title="Optimize prompt with AI" style={{ padding: "8px 10px", background: "none", border: `1px solid ${optimizing ? T.acc : T.brd}`, borderRadius: 6, cursor: input.trim() && !optimizing ? "pointer" : "not-allowed", color: optimizing ? T.acc : T.txD, fontSize: 14, opacity: input.trim() ? 1 : 0.3 }}>{optimizing ? '...' : '✨'}</button>
              {/* Save as template button */}
              <button onClick={() => setShowSavePrompt(!showSavePrompt)} title="Save as prompt template" style={{ padding: "8px 10px", background: "none", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", color: T.txD, fontSize: 14 }}>💾</button>
              <Btn v="primary" disabled={loading || !input.trim()} onClick={() => sendMessage()}>Send</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
